import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import Helmet from '../lib/Helmet';
import { CATALOGUE } from '../data/catalogue';
import { cleanIds, compareHref, readTray, writeTray, compareVariants, COMPARE_CTX } from '../lib/compareTray';
import {
  COMPARE_SLUG, COMPARE_TITLE, COMPARE_META, DEFAULT_ANSWERS, MAX_COMPARE,
  toUnits, baseUnits, defaultPicks, defaultSize, allPairs, comparableSizes, sizeSlug, findPair, pairH1, pairTitle, pairMeta,
  sizeFromSlug, sizeTitle, sizeH1, sizeMeta, tonText, unitName, kindText, STAR_FOOTNOTE,
} from '../data/compare';
import { formatINR } from '../lib/quote';
import PickTray from '../components/compare/PickTray';
import AddSheet from '../components/compare/AddSheet';
import QuickAnswer from '../components/compare/QuickAnswer';
import KeyDifferences from '../components/compare/KeyDifferences';
import CompareTable from '../components/compare/CompareTable';
import CompareFaq from '../components/compare/CompareFaq';

// Routes: /compare-ac (the tool), /compare-1-5-ton-ac (one per size, from
// tonnageOptions()), /compare/<a>-vs-<b> (one per pair). Same page, the tray
// pre-filled differently. Prices and stock come only from pricing.js, via
// compareVariants(): every Daikin model with and without the pipe kit, and
// the clearance units in stock.

const parseIds = (search) => cleanIds((new URLSearchParams(search).get('ac') || '').split(',').filter(Boolean));

export default function CompareAC() {
  const { pair } = useParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState(DEFAULT_ANSWERS);
  // The add sheet: closed, open to add, or open to change one AC ({ replace: id }).
  const [sheet, setSheet] = useState(null);

  const variants = useMemo(() => compareVariants(), []);
  const all = useMemo(() => toUnits(variants, answers, COMPARE_CTX), [variants, answers]);
  // Size pages, pair pages and the defaults use the kit sold by default.
  const base = useMemo(() => baseUnits(all), [all]);
  // The "+ Add AC" list: one entry per model, plus clearance; the kit is
  // switched on the table itself.
  const pickable = useMemo(() => all.filter((u) => u.isDefault), [all]);

  const sizes = comparableSizes(CATALOGUE);
  const size = sizeFromSlug(pathname.replace(/^\/+|\/+$/g, ''));
  const mode = pair ? 'pair' : size ? 'size' : 'tool';
  const sizeUnits = useMemo(() => (size ? base.filter((u) => u.model.soldAs === size) : []), [base, size]);
  const found = mode === 'pair' ? findPair(base, pair) : null;

  // Did the page open on our suggestion rather than the visitor's own picks?
  const startedOnSuggestion = useMemo(
    () => mode === 'tool' && !parseIds(search).length && !readTray().length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, pathname],
  );
  const [suggested, setSuggested] = useState(startedOnSuggestion);
  useEffect(() => { setSuggested(startedOnSuggestion); }, [startedOnSuggestion]);

  // What the page opens with: the URL, then the saved tray, then a default.
  const initial = useMemo(() => {
    const fromUrl = parseIds(search);
    if (mode === 'pair') return found ? [found.a.id, found.b.id] : [];
    if (mode === 'size') return fromUrl.length ? fromUrl : defaultPicks(sizeUnits).map((u) => u.id);
    if (fromUrl.length) return fromUrl;
    const saved = readTray();
    if (saved.length) return saved;
    const s = defaultSize(base);
    return defaultPicks(base.filter((u) => u.model.soldAs === s)).map((u) => u.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, pathname]);

  const [ids, setIds] = useState(initial);
  useEffect(() => { setIds(initial); }, [initial]);

  // Changes on a size or pair page move to the tool, so those pages stay
  // fixed for Google. On the tool, the URL and the tray follow the picks.
  const update = useCallback((next) => {
    setSuggested(false);
    const clean = cleanIds(next);
    writeTray(clean);
    if (mode !== 'tool') { navigate(compareHref(clean)); return; }
    setIds(clean);
    navigate(`${pathname}?ac=${clean.join(',')}`, { replace: true });
  }, [mode, navigate, pathname]);

  const units = useMemo(() => ids.map((id) => all.find((u) => u.id === id)).filter(Boolean), [ids, all]);
  const answerUnits = mode === 'size' ? sizeUnits : units;
  const popular = useMemo(() => allPairs(base).slice(0, 8), [base]);
  const swapKit = (fromId, toId) => update(ids.map((x) => (x === fromId ? toId : x)));

  useEffect(() => {
    if (mode === 'tool') return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.citicool.in/' },
        { '@type': 'ListItem', position: 2, name: 'Compare ACs', item: `https://www.citicool.in/${COMPARE_SLUG}` },
        { '@type': 'ListItem', position: 3, name: mode === 'pair' && found ? pairH1(found.a, found.b) : sizeH1(size), item: `https://www.citicool.in${pathname}` },
      ],
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [mode, pathname, found, size]);

  if (mode === 'pair' && !found) return <Navigate to={`/${COMPARE_SLUG}`} replace />;
  if (mode === 'size' && !sizes.includes(size)) return <Navigate to={`/${COMPARE_SLUG}`} replace />;

  const title = mode === 'pair' ? pairTitle(found.a, found.b) : mode === 'size' ? sizeTitle(size) : COMPARE_TITLE;
  const meta = mode === 'pair' ? pairMeta(found.a, found.b) : mode === 'size' ? sizeMeta(size, sizeUnits.length) : COMPARE_META;
  const h1 = mode === 'pair' ? pairH1(found.a, found.b) : mode === 'size' ? sizeH1(size) : 'Compare ACs side by side';
  const line = mode === 'size'
    ? `All ${sizeUnits.length} ${tonText(size)} ACs we sell. The answer looks at every one of them.`
    : `Pick up to ${MAX_COMPARE} ACs. We tell you which one to buy.`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={meta} />
      </Helmet>

      <main className="mx-auto max-w-5xl px-4 pt-20 pb-16 lg:pt-24" data-testid="compare-page">
        <nav className="text-[11px] text-gray-500 sm:text-xs">
          <Link to="/" className="hover:text-blue-600">Home</Link>
          <span className="mx-1.5">›</span>
          {mode === 'tool' ? <span className="text-gray-700">Compare ACs</span> : (
            <>
              <Link to={`/${COMPARE_SLUG}`} className="hover:text-blue-600">Compare ACs</Link>
              <span className="mx-1.5">›</span>
              <span className="text-gray-700">{mode === 'size' ? tonText(size) : 'This pair'}</span>
            </>
          )}
        </nav>
        <h1 className="mt-1 text-xl font-semibold text-gray-900 sm:text-3xl" data-testid="compare-h1">{h1}</h1>
        <p className="mt-1 text-xs text-gray-600 sm:text-sm">{line}</p>

        {/* Clarity, Oct 2026: the tool opened with three of our picks, so a
            visitor could add only one of their own before it was full. Now
            it says these are our suggestion, every AC has Change, and one
            tap clears the lot to pick your own. */}
        {suggested && units.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 sm:px-4" data-testid="compare-suggestion">
            <p className="text-xs text-blue-900 sm:text-sm">
              <span className="font-semibold">Our suggestion to start with.</span> Change any of them, or pick your own.
            </p>
            <button type="button" data-testid="compare-pick-own"
              onClick={() => { update([]); setSheet({ replace: null }); }}
              className="rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-blue-700 sm:text-sm">
              Pick your own ACs
            </button>
          </div>
        )}

        <PickTray units={units}
          onRemove={(id) => update(ids.filter((x) => x !== id))}
          onAdd={() => setSheet({ replace: null })}
          onChange={(id) => setSheet({ replace: id })} />
        <AddSheet open={!!sheet} onClose={() => setSheet(null)} units={pickable} selected={ids}
          replacing={sheet && sheet.replace ? units.find((u) => u.id === sheet.replace) : null}
          onPick={(id) => {
            if (sheet && sheet.replace) {
              // Change in place: the new AC takes the old one's slot.
              update(ids.includes(id) ? ids : ids.map((x) => (x === sheet.replace ? id : x)));
              setSheet(null);
              return;
            }
            // Adding: the list stays open so several can be picked in one go;
            // its footer button closes it.
            update(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
          }} />
        {units.length < 2 && (
          <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2.5 text-center text-xs text-gray-600 sm:text-sm" data-testid="compare-need-more">
            {units.length === 0 ? 'Add two or more ACs to see them side by side.' : 'Add one more AC to see them side by side.'}
          </p>
        )}

        <KeyDifferences units={units} answers={answers} />

        <QuickAnswer units={answerUnits} answers={answers} onChange={setAnswers} />

        <CompareTable units={units} onKit={swapKit} />
        {units.some((u) => u.clearance) && <p className="mt-2 text-[11px] leading-relaxed text-gray-500">{STAR_FOOTNOTE}</p>}

        {mode === 'size' && (
          <section className="mt-8" data-testid="size-all-list">
            <h2 className="text-base font-bold text-gray-900 md:text-lg">All {tonText(size)} ACs</h2>
            <ul className="mt-2 divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
              {sizeUnits.map((u) => {
                const on = ids.includes(u.id);
                return (
                  <li key={u.id} className="flex items-center gap-3 px-3 py-2.5">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-gray-900">{unitName(u)} <span className="font-normal text-gray-500">· {kindText(u)} · {u.model.kw.toFixed(2)} kW</span></span>
                      <span className="block text-xs text-gray-500">{formatINR(u.price)} · {u.inStock ? 'In stock' : u.soldOut ? 'Sold out' : 'On order'}</span>
                    </span>
                    <button type="button" disabled={!on && ids.length >= MAX_COMPARE} data-testid={`size-add-${u.id}`}
                      onClick={() => update(on ? ids.filter((x) => x !== u.id) : [...ids, u.id])}
                      className={`shrink-0 rounded-md border px-3 py-1.5 text-xs font-semibold disabled:opacity-40 ${on ? 'border-gray-300 text-gray-600' : 'border-blue-600 text-blue-700 hover:bg-blue-50'}`}>
                      {on ? 'Remove' : 'Add to compare'}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <section className="mt-8" data-testid="popular-comparisons">
          <h2 className="text-base font-bold text-gray-900 md:text-lg">Popular comparisons</h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {popular.map((p) => (
              <Link key={p.slug} to={`/${p.slug}`}
                className="rounded-full border border-gray-300 px-3.5 py-1.5 text-xs text-gray-700 transition hover:border-blue-400 hover:text-blue-700">
                {unitName(p.a)} vs {p.a.brand === p.b.brand ? unitName(p.b).replace(`${p.b.brand} `, '') : unitName(p.b)}
                <span className="text-gray-500"> · {tonText(p.a.model.soldAs)}</span>
              </Link>
            ))}
          </div>
          {sizes.length > 0 && (
            <>
              <h3 className="mt-4 text-sm font-semibold text-gray-900">Compare by size</h3>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="size-links">
                {sizes.map((s) => (
                  <Link key={s} to={`/${sizeSlug(s)}`}
                    className="rounded-full border border-gray-300 px-3.5 py-1.5 text-xs text-gray-700 transition hover:border-blue-400 hover:text-blue-700">
                    All {tonText(s)} ACs
                  </Link>
                ))}
              </div>
            </>
          )}
        </section>

        <CompareFaq />

        <p className="mt-8 text-sm text-gray-700">
          Not sure what size you need?{' '}
          <Link to="/ac-calculator" className="font-semibold text-blue-700 underline" data-testid="link-calculator">Use our AC calculator.</Link>
        </p>
        {mode === 'tool' && units.length > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            Share this comparison: the link in your address bar opens the same ACs. Prices are what you pay us, GST included, today.
          </p>
        )}
      </main>
    </>
  );
}
